import { PERSONA_FACE_TILES } from '../data/personaFaceMetadata';
import {
  inferGenderHintFromName,
  VOICE_TO_GENDER,
  type GenderHint,
} from '@akoreps/persona-identity';

export function inferGenderForFace(
  name: string,
  voice: string | null | undefined,
  gender?: string | null,
): GenderHint {
  const g = (gender ?? '').trim().toLowerCase();
  if (g === 'female') return 'female';
  if (g === 'male') return 'male';
  if (voice && VOICE_TO_GENDER[voice]) {
    return VOICE_TO_GENDER[voice];
  }
  return inferGenderHintFromName(name);
}

function fnv1a32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export type PersonaFaceAssignInput = {
  id: string;
  name: string;
  voice?: string | null;
  /** Stored gender; preferred over voice/name inference when present. */
  gender?: string | null;
};

/**
 * Deterministic per-session face tile ids (`persona_01` ... `persona_16`), distinct
 * within the session and gender-matched when possible.
 */
export function assignPersonaFaceIds(input: {
  sessionId: string;
  personas: PersonaFaceAssignInput[];
}): Map<string, string> {
  const { sessionId, personas } = input;
  const result = new Map<string, string>();
  if (personas.length === 0) return result;

  const sorted = [...personas].sort((a, b) => {
    const ka = fnv1a32(`${sessionId}::${a.id}`);
    const kb = fnv1a32(`${sessionId}::${b.id}`);
    return ka === kb ? a.id.localeCompare(b.id) : ka - kb;
  });

  const used = new Set<string>();

  for (const p of sorted) {
    const hint = inferGenderForFace(p.name, p.voice, p.gender);
    const available = PERSONA_FACE_TILES.filter((t) => !used.has(t.persona_id));
    if (available.length === 0) break;

    const genderMatch = available.filter((t) => t.gender_presentation === hint);
    const candidates = genderMatch.length > 0 ? genderMatch : available;

    const offset = fnv1a32(`${sessionId}::${p.id}::face`) % candidates.length;
    let chosen: string | undefined;
    for (let i = 0; i < candidates.length; i++) {
      const t = candidates[(offset + i) % candidates.length];
      if (!used.has(t.persona_id)) {
        chosen = t.persona_id;
        break;
      }
    }
    if (!chosen) {
      chosen = available[0]?.persona_id;
    }
    if (chosen) {
      used.add(chosen);
      result.set(p.id, chosen);
    }
  }

  return result;
}

export function personaFacePublicPath(personaImageId: string): string {
  return `/persona-faces/${personaImageId}.png`;
}
